var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useState, } from 'react';
import { format, isValid } from 'date-fns';
import MonthPopover from './components/MonthPopover';
import YearPopover from './components/YearPopover';
import { Input } from '../Input';
import { Popper } from '../Popper';
import { transformFromStringToDateMask, transformFromDateMaskToValidDate, formatDateRange, validateAndReturnFormattedAndValidDate, DEFAULT_WEEK_DAYS, getInitialValues, } from './utils/logic';
import { InputTypes, MASKED_DATE_LENGTH, } from './interfaces';
import Calendar from '../../assets/icons/Calendar';
import * as S from './styles';
import { Header } from './components/Header';
import { useMonthMatrix } from '../../hooks/useMonthMatrix';
import { useYearRange } from '../../hooks/useYearRange';
import { buildDatePickerProps } from './modifiers';
import { useTheme } from '../../themes';
export var DatePicker = forwardRef(function (_a, ref) {
    var className = _a.className, _b = _a.defaultValue, defaultValue = _b === void 0 ? {} : _b, _c = _a.value, value = _c === void 0 ? {} : _c, inputStartProps = _a.inputStartProps, inputEndProps = _a.inputEndProps, _d = _a.hasEndDate, hasEndDate = _d === void 0 ? false : _d, footer = _a.footer, initialDate = _a.initialDate, _e = _a.locale, locale = _e === void 0 ? 'ptBR' : _e, _f = _a.weekDays, unsanitizedWeekDays = _f === void 0 ? DEFAULT_WEEK_DAYS[locale] : _f, _g = _a.onChange, onChange = _g === void 0 ? function () { return []; } : _g, style = _a.style, rest = __rest(_a, ["className", "defaultValue", "value", "inputStartProps", "inputEndProps", "hasEndDate", "footer", "initialDate", "locale", "weekDays", "onChange", "style"]);
    var _h = useState(''), startDate = _h[0], setStartDate = _h[1];
    var _j = useState(''), endDate = _j[0], setEndDate = _j[1];
    var _k = useState([]), selectedDates = _k[0], setSelectedDates = _k[1];
    var _l = useState(null), containerRef = _l[0], setContainerRef = _l[1];
    var _m = useState(false), isOpen = _m[0], setIsOpen = _m[1];
    var _o = useState(false), isYearPopover = _o[0], setIsYearPopover = _o[1];
    var weekDays = unsanitizedWeekDays === null || unsanitizedWeekDays === void 0 ? void 0 : unsanitizedWeekDays.slice(0, 7);
    var _p = getInitialValues({
        initial: initialDate,
        value: value,
        defaultValue: defaultValue,
    }), initialYear = _p.initialYear, initialMonth = _p.initialMonth;
    var defaultDateValues = {
        initialYear: initialYear,
        initialMonth: initialMonth,
    };
    var _q = useMonthMatrix({
        initialYear: defaultDateValues.initialYear,
        initialMonth: defaultDateValues.initialMonth,
        locale: locale,
    }), monthMatrix = _q.monthMatrix, month = _q.month, nextMonth = _q.nextMonth, prevMonth = _q.prevMonth, changeYear = _q.changeYear;
    var _r = useYearRange(defaultDateValues.initialYear), titleRange = _r.titleRange, yearRange = _r.yearRange, nextYears = _r.nextYears, prevYears = _r.prevYears;
    var headerTitle = isYearPopover ? titleRange : month;
    var prevAction = isYearPopover ? prevYears : prevMonth;
    var nextAction = isYearPopover ? nextYears : nextMonth;
    var toggleDropdown = function () {
        setIsOpen(function (old) { return !old; });
    };
    var handleSetValues = useCallback(function (dates) {
        if (dates.every(function (date) { return isValid(date); })) {
            var formatDate = dates.map(function (d) { return formatDateRange(d); });
            setSelectedDates(formatDate);
        }
    }, []);
    useImperativeHandle(ref, function () { return ({
        setValues: handleSetValues,
        value: selectedDates,
    }); }, [selectedDates]);
    var handleChange = function (value, type, open) {
        if (open === void 0) { open = true; }
        open && setIsOpen(true);
        var maskedDate = transformFromStringToDateMask(value);
        if (type === InputTypes.START)
            setStartDate(maskedDate);
        if (type === InputTypes.END && hasEndDate)
            setEndDate(maskedDate);
        if (!(maskedDate.length === MASKED_DATE_LENGTH))
            return;
        var formatDate = transformFromDateMaskToValidDate(maskedDate);
        var isValidDate = isValid(formatDate);
        if (isValidDate) {
            var tmpArray = __spreadArray([], selectedDates, true);
            if (type === InputTypes.START)
                tmpArray[0] = formatDate;
            if (type === InputTypes.END && hasEndDate)
                tmpArray[1] = formatDate;
            setSelectedDates(tmpArray);
        }
    };
    useEffect(function () {
        var tmpArray = __spreadArray([], selectedDates, true);
        if ((value === null || value === void 0 ? void 0 : value.start) || (defaultValue === null || defaultValue === void 0 ? void 0 : defaultValue.start)) {
            var _a = validateAndReturnFormattedAndValidDate(String(value.start || defaultValue.start)), valid = _a.valid, formatted = _a.formatted, date = _a.date;
            if (valid) {
                tmpArray[0] = date;
                setStartDate(formatted);
            }
        }
        if ((value === null || value === void 0 ? void 0 : value.end) || (defaultValue === null || defaultValue === void 0 ? void 0 : defaultValue.end)) {
            var _b = validateAndReturnFormattedAndValidDate(String(value.end || defaultValue.end)), valid = _b.valid, formatted = _b.formatted, date = _b.date;
            if (valid) {
                tmpArray[1] = date;
                setEndDate(formatted);
            }
        }
        setSelectedDates(tmpArray);
    }, []);
    useEffect(function () {
        if (selectedDates.length) {
            if (selectedDates === null || selectedDates === void 0 ? void 0 : selectedDates[0]) {
                var dateStart = format(selectedDates[0], 'dd/MM/yyyy');
                setStartDate(dateStart);
            }
            if (hasEndDate && (selectedDates === null || selectedDates === void 0 ? void 0 : selectedDates[1])) {
                setEndDate(format(selectedDates[1], 'dd/MM/yyyy'));
            }
            else {
                setEndDate('');
            }
        }
        else {
            setStartDate('');
        }
    }, [selectedDates]);
    useEffect(function () {
        onChange(selectedDates);
    }, [selectedDates]);
    var toggleYearOrMonthPopover = function () {
        setIsYearPopover(function (old) { return !old; });
    };
    var theme = useTheme();
    var styles = buildDatePickerProps(style, theme);
    var dayStyles = {
        dayContainerStyle: style === null || style === void 0 ? void 0 : style.dayContainerStyle,
        dayTextStyle: style === null || style === void 0 ? void 0 : style.dayTextStyle,
        dayBetweenRangeContainerStyle: style === null || style === void 0 ? void 0 : style.dayBetweenRangeContainerStyle,
    };
    return (_jsxs(S.Container, __assign({ className: className, ref: setContainerRef }, rest, { children: [_jsxs(S.Wrapper, { children: [_jsx(Input, __assign({ placeholder: "00/00/0000", iconRight: _jsx(Calendar, {}), iconRightAction: toggleDropdown, value: startDate }, inputStartProps, { onChange: function (e) { return handleChange(e.target.value, InputTypes.START); } })), hasEndDate && (_jsx(Input, __assign({ mLeft: "nano", placeholder: "00/00/0000", iconRight: _jsx(Calendar, {}), iconRightAction: toggleDropdown, value: endDate }, inputEndProps, { onChange: function (e) { return handleChange(e.target.value, InputTypes.END); } })))] }), _jsx(Popper, __assign({ referenceElement: containerRef, containerWidth: true, placement: "bottom-start", open: isOpen, onClose: function () { return setIsOpen(false); } }, { children: _jsxs(S.PopoverContainer, __assign({ style: styles.datePickerContainerStyle }, { children: [_jsx(Header, { handleChangePickerType: toggleYearOrMonthPopover, yearIsOpen: isYearPopover, title: headerTitle, prev: prevAction, next: nextAction, style: {
                                headerTextStyle: style === null || style === void 0 ? void 0 : style.headerTextStyle,
                                headerIndicatorStyle: style === null || style === void 0 ? void 0 : style.headerIndicatorStyle,
                                headerActionButtonStyle: style === null || style === void 0 ? void 0 : style.headerActionButtonStyle,
                            } }), !isYearPopover ? (_jsx(MonthPopover, { monthMatrix: monthMatrix, setSelectedDates: setSelectedDates, selectedDates: selectedDates, footer: footer, weekDays: weekDays, hasEndDate: hasEndDate, style: __assign({ weekTextStyle: style === null || style === void 0 ? void 0 : style.weekTextStyle }, dayStyles) })) : (_jsx(YearPopover, { yearRange: yearRange, selectedDates: selectedDates, changeCurrentYear: function (currentYear) {
                                changeYear(currentYear);
                                toggleYearOrMonthPopover();
                            }, style: dayStyles }))] })) }))] })));
});
